import {
  createContext,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getActiveBranchId, setActiveBranchId } from "@/utils/branchStore";
import {
  getIsMultiBranch,
  getLiffBranch,
  getMembershipBranches,
} from "@/services";
import { COLORS, TanstackQueryKey } from "@/constants";
import { ENTRY_LIFF_ID_SESSION_KEY } from "@/config/liff";
import { useAuth } from "./AuthContext";

// LIFF id this session entered through (written by src/config/liff.ts). No
// session entry id → no LIFF lookup (localStorage.liffId may be stale).
const readEntryLiffId = (): string | null => {
  try {
    return globalThis.sessionStorage.getItem(ENTRY_LIFF_ID_SESSION_KEY);
  } catch {
    return null;
  }
};

const isMarked = (key: string): boolean => {
  try {
    return !!globalThis.sessionStorage.getItem(key);
  } catch {
    return false;
  }
};

const mark = (key: string): void => {
  try {
    globalThis.sessionStorage.setItem(key, "1");
  } catch {
    // sessionStorage unavailable — the in-memory ref below still holds it
  }
};

// code comes straight from the BE MembershipBranch payload; My Booking uses it
// for the "code · name" branch display (QTY-1871).
export type Branch = { id: string; name: string; code?: string };

type LineBranchContextType = {
  selectedBranchId: string;
  branches: Branch[];
  isMultiBranch: boolean;
  isLoading: boolean;
  switchBranch: (id: string) => void;
  /**
   * Align the selector's React state to branchStore's current value.
   *
   * Unlike switchBranch, this does NOT change branchStore or the outgoing
   * branch-id header (it only reads it), and it does NOT cancel/invalidate
   * queries — so it can't itself surface other-branch data. Use it after
   * resetting branchStore (e.g. clearActiveBranchId on login) to make the
   * selector reflect the reset. If you need queries to refetch, clear or
   * invalidate them yourself first (LoginPage clears the whole cache).
   */
  reseedFromStore: () => void;
};

const LineBranchContext = createContext<LineBranchContextType | null>(null);

export const LineBranchProvider = ({
  children,
}: {
  children: ReactNode;
}): ReactElement => {
  const queryClient = useQueryClient();
  const { user, loading: authLoading } = useAuth();
  const isAuthenticated = !!user && !authLoading;

  const [selectedBranchId, setSelectedBranchId] = useState<string>(
    getActiveBranchId(),
  );

  // Re-seed from branchStore whenever the member identity changes (logging in
  // as a different member on a shared device, or logout→relogin). This
  // provider is mounted above the router (App.tsx) so it never remounts on
  // navigation, and the login/logout paths call clearActiveBranchId() — which
  // resets the store to "1" but NOT this React state. Without re-seeding, the
  // selector would keep the previous member's branch while axios sends the
  // cleared branch-id header, and the reconcile effect below can't correct it
  // when the new member's branch list happens to contain the old id (the
  // selection reads as "valid"). Keyed on uid string, so a token refresh that
  // preserves the uid never fires it.
  const uid = user?.uid ?? null;
  useEffect(() => {
    setSelectedBranchId(getActiveBranchId());
  }, [uid]);

  const { data: multiBranchData, isLoading: isMultiBranchLoading } = useQuery({
    queryKey: [TanstackQueryKey.IS_MULTI_BRANCH],
    queryFn: getIsMultiBranch,
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });

  const isMultiBranch = multiBranchData?.isMultiBranch ?? false;

  // membership-branches throws when the member has no active membership; treat
  // any failure as "no selectable branches" rather than surfacing an error.
  const { data: branchData, isLoading: isBranchesLoading } = useQuery({
    queryKey: [TanstackQueryKey.MEMBERSHIP_BRANCHES],
    queryFn: getMembershipBranches,
    enabled: isAuthenticated && isMultiBranch,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const branches = useMemo<Branch[]>(
    () =>
      (branchData ?? []).map((b) => ({
        id: String(b.id),
        name: b.name,
        code: b.code,
      })),
    [branchData],
  );

  // Start on the branch whose LIFF the member entered through (per-branch LINE
  // OAs). Decided BEFORE the first branch is chosen: while the lookup is
  // pending the reconcile below waits. Applied once per member + LIFF id per
  // session so a later manual switch isn't snapped back.
  const entryLiffId = readEntryLiffId();
  const liffAppliedRef = useRef<string | null>(null);
  const liffAppliedKey =
    uid && entryLiffId ? `liff-branch-applied:${uid}:${entryLiffId}` : null;
  const liffQueryEnabled =
    isAuthenticated &&
    isMultiBranch &&
    !!liffAppliedKey &&
    liffAppliedRef.current !== liffAppliedKey &&
    !isMarked(liffAppliedKey);
  const {
    data: liffBranchData,
    isSuccess: isLiffBranchSuccess,
    isError: isLiffBranchError,
    isLoading: isLiffBranchLoading,
  } = useQuery({
    queryKey: [TanstackQueryKey.LIFF_BRANCH, entryLiffId],
    queryFn: () => getLiffBranch(entryLiffId as string),
    enabled: liffQueryEnabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const isLiffBranchPending = liffQueryEnabled && isLiffBranchLoading;

  // Pick the branch once the list (and the LIFF lookup, if any) resolves:
  // LIFF branch (if permitted) ?? current valid selection ?? first permitted.
  // A failed lookup is still "decided" (marked, never refetched) and falls
  // through to the default.
  useEffect(() => {
    if (branches.length === 0 || isLiffBranchPending) return;
    let target: string | undefined;
    if (
      liffQueryEnabled &&
      (isLiffBranchSuccess || isLiffBranchError) &&
      liffAppliedKey
    ) {
      liffAppliedRef.current = liffAppliedKey;
      mark(liffAppliedKey);
      const liffId = liffBranchData?.branchId;
      if (liffId != null && branches.some((b) => b.id === String(liffId))) {
        target = String(liffId);
      }
    }
    const isSelectionValid = branches.some((b) => b.id === selectedBranchId);
    target ??= isSelectionValid ? selectedBranchId : branches[0].id;
    if (target !== selectedBranchId) {
      setSelectedBranchId(target);
      setActiveBranchId(target);
      // Queries fired before the branch list resolved carried the old
      // branch-id header — drop them so every screen refetches under the
      // chosen branch, same as an explicit switchBranch().
      // cancelQueries first: a stale-branch fetch still in flight would
      // otherwise swallow the invalidate (the refetch dedupes onto it) and
      // its wrong-branch response would stay on screen.
      queryClient.cancelQueries();
      queryClient.invalidateQueries();
    }
  }, [
    branches,
    selectedBranchId,
    queryClient,
    isLiffBranchPending,
    liffQueryEnabled,
    isLiffBranchSuccess,
    isLiffBranchError,
    liffBranchData,
    liffAppliedKey,
  ]);

  const switchBranch = (id: string) => {
    setSelectedBranchId(id);
    setActiveBranchId(id);
    // Same cancel-then-invalidate as the reconcile effect: without the cancel,
    // an in-flight fetch for the previous branch dedupes the refetch and its
    // old-branch response wins.
    queryClient.cancelQueries();
    queryClient.invalidateQueries();
  };

  // Re-seed selectedBranchId from branchStore on demand. The uid-change effect
  // above covers a login as a DIFFERENT member; this closes the SAME-member
  // re-login case (uid unchanged, so that effect never fires) without a full
  // page reload — LoginPage calls it after clearActiveBranchId(). The reconcile
  // effect then snaps to the member's default branch.
  const reseedFromStore = useCallback(() => {
    setSelectedBranchId(getActiveBranchId());
  }, []);

  const value = useMemo<LineBranchContextType>(
    () => ({
      selectedBranchId,
      branches,
      isMultiBranch,
      isLoading:
        isMultiBranchLoading || isBranchesLoading || isLiffBranchPending,
      switchBranch,
      reseedFromStore,
    }),
    // switchBranch is stable enough for this context's consumers
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      selectedBranchId,
      branches,
      isMultiBranch,
      isMultiBranchLoading,
      isBranchesLoading,
      isLiffBranchPending,
    ],
  );

  return (
    <LineBranchContext.Provider value={value}>
      {children}
      {isLiffBranchPending && (
        // Overlay (not a swap: this provider sits above the router, so
        // replacing children would remount the whole app) until the entry
        // LIFF's branch is known, so a returning member isn't shown their
        // stored branch and then switched mid-flow. Same look as
        // ProtectedRoute's auth "Loading...".
        <div
          aria-busy="true"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 2147483647,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            color: COLORS.text.pagePrimary,
            backgroundColor: COLORS.background.bodyDefault,
          }}
        >
          Loading...
        </div>
      )}
    </LineBranchContext.Provider>
  );
};

export const useLineBranch = (): LineBranchContextType => {
  const ctx = useContext(LineBranchContext);
  if (!ctx)
    throw new Error("useLineBranch must be used within LineBranchProvider");
  return ctx;
};
